import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { RHFTextField } from "../../../../../RHFComponents/RHFComponents";
import { defNotePrestAgenda, medpgmpn } from "../../../../../../networking";
import dayjs from "dayjs";
import { AllertMessage } from "./MedintAgendaForm/MedintAgendaFormDetail";

//Props
type Props = {
  open: boolean;
  onClose: () => void;
  parentMethods?: UseFormReturn<any>;
  selectedRow?: any;
};

//Campi del form
type NoteForm = {
  note: string;
  link: string;
};

type CRUD = "c" | "u" | undefined;

//Serve per definire l'azione che chiamerà l'allert e la specifica funzione
type ConfirmAction = "save" | "default" | null;

// Lunghezza massima dei caratteri per le textfield
const MAX_NOTE_LENGTH = 400;
const MAX_LINK_LENGTH = 400;

const MedintAgendaPrestazioneNotesModal = ({
  open,
  onClose,
  selectedRow,
}: Props) => {
  const modalMethods = useForm<NoteForm>({
    defaultValues: {
      note: "",
      link: "",
    },
    mode: "onChange",
  });

  //Metodi del form
  const { control, watch, reset, handleSubmit } = modalMethods;

  const noteValue = watch("note") ?? "";
  const linkValue = watch("link") ?? "";

  //Stato che prende i tipi di operazioni CRUD e aiuta a gestire l'azione da eseguire
  const [crudOperation, setCrudOperation] = useState<CRUD>(undefined);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState<boolean>(false);

  //Stato che definisce l'azione da eseguire sul modale in base al tasto cliccato
  //(Scarica Default o Salvataggio, che comprende sia inserimento che modifica)
  //Serve per mandare il corretto messaggio dell'allert e la funzione da eseguire
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const getNoteData = async () => {
    try {
      const response = await medpgmpn({
        crud: "r",
        medpgmp_prgint: selectedRow?.idMedpgmp ?? "",
      });

      //Se restituisce ko significa che non esistono note per la prestazione, pertanto andranno create
      if (response?.data?.esito === "KO") {
        setCrudOperation("c");
        return;
      } else {
        setCrudOperation("u");
      }

      //Se ho i dati del medpgmpn, li metto nel form, altrimenti li valorizzo a stringa vuota
      reset({
        note: response?.data?.dati?.medpgmpn?.MEDPGMPN_NOTA_PREST_AGENDA ?? "",
        link: response?.data?.dati?.medpgmpn?.MEDPGMPN_LK_NT_PREST_AGENDA ?? "",
      });
    } catch (error) {
      console.error("Error fetching note data:", error);
    }
  };

  //All'apertura del componente recupero le note della prestazione
  useEffect(() => {
    if (!open) return;
    console.log({ selectedRow });
    getNoteData();
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const askConfirmData = (action: ConfirmAction) => {
    setConfirmAction(action);

    if (action === "save") {
      setMessage(
        crudOperation === "c"
          ? "Confermare l'inserimento delle note?"
          : "Confermare la modifica delle note?",
      );
    }

    if (action === "default") {
      setMessage("Confermare lo scarico delle note di default?");
    }

    setSeverity("warning");
    setShowAllert(true);
  };

  const onSubmit = async (values: NoteForm) => {
    console.log("Valori modal:", values);
    console.log("Riga selezionata:", selectedRow);

    setLoading(true);

    try {
      const response = await medpgmpn({
        crud: crudOperation,
        medpgmp_prgint: selectedRow?.idMedpgmp ?? "",
        dati: {
          medpgmpn: {
            MEDPGMPN_NOTA_PREST_AGENDA: values.note,
            MEDPGMPN_PID_ACU: "0",
            MEDPGMPN_TIMESTAMP: dayjs().format("YYYY-MM-DD HH:mm:ss.SSS"),
            MEDPGMPN_LK_NT_PREST_AGENDA: values.link,
          },
        },
      });

      if (response?.data?.esito === "OK") {
        setSeverity("success");
        setMessage(response?.data?.messaggio ?? "Operazione completata");
      } else {
        setSeverity("error");
        setMessage(
          response?.data?.messaggio ?? "Errore durante il salvataggio",
        );
      }

      setShowAllert(true);
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore durante il salvataggio");
      setShowAllert(true);
    } finally {
      setLoading(false);
    }
  };

  //Funzione per scaricare le note di default. A volte arrivano come stringhe vuote
  const getDefaultNote = async () => {
    setLoading(true);

    try {
      const response = await defNotePrestAgenda({
        codSpecia: selectedRow?.specialita ?? "",
        codAnapre: selectedRow?.prestazione ?? "",
        codTpgpre: selectedRow?.codiceAgenda ?? "",
      });

      console.log(response.data.dati);

      if (response?.data?.esito === "OK") {
        reset({
          note: response?.data?.dati?.notaAmministrativa ?? "",
          link: response?.data?.dati?.linkNotaAmministrativa ?? "",
        });

        setSeverity("success");
        setMessage(
          response?.data?.messaggio ?? "Note di default caricate correttamente",
        );
      } else {
        setSeverity("error");
        setMessage(
          response?.data?.messaggio ??
            "Errore durante il recupero delle note di default",
        );
      }

      setShowAllert(true);
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore durante il recupero delle note di default");
      setShowAllert(true);
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  const handleAlertConfirm = () => {
    if (confirmAction === "save") {
      handleSubmit(onSubmit)();
      return;
    }

    if (confirmAction === "default") {
      getDefaultNote();
    }
  };

  const sxCounter = {
    fontSize: "12px",
    color: "text.secondary",
    mt: 0.5,
    ml: 1,
  };

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={handleAlertConfirm}
      />
      <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          if (reason === "escapeKeyDown") return;
          handleClose();
        }}
      >
        <Box
          sx={{
            width: "70%",
            maxWidth: "1100px",
            minWidth: "700px",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            borderRadius: "4px",
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            px: 3,
            py: 2.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              mb: 2,
              color: "#2C2C2C",
            }}
          >
            Note Prestazione / Agenda
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RHFTextField
                name="note"
                control={control}
                label="Note prestazione / agenda"
                placeholder="Note eventuali"
                fullWidth
                multiline
                minRows={3}
                maxRows={5}
                rules={{
                  maxLength: {
                    value: MAX_NOTE_LENGTH,
                    message: `Max ${MAX_NOTE_LENGTH} caratteri`,
                  },
                }}
                inputProps={{ maxLength: MAX_NOTE_LENGTH }}
              />
              <Typography sx={sxCounter}>
                ({noteValue.length}/{MAX_NOTE_LENGTH})
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <RHFTextField
                name="link"
                control={control}
                label="Link"
                placeholder="Inserire link"
                fullWidth
                multiline
                minRows={3}
                maxRows={5}
                rules={{
                  maxLength: {
                    value: MAX_LINK_LENGTH,
                    message: `Max ${MAX_LINK_LENGTH} caratteri`,
                  },
                }}
                inputProps={{ maxLength: MAX_LINK_LENGTH }}
              />
              <Typography sx={sxCounter}>
                ({linkValue.length}/{MAX_LINK_LENGTH})
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Button
                  variant="outlinedCustom"
                  onClick={handleClose}
                  size="small"
                >
                  Indietro
                </Button>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlinedCustom"
                    onClick={() => askConfirmData("default")}
                    size="small"
                  >
                    Scarica Default
                  </Button>

                  <Button
                    variant="containedCustom"
                    onClick={handleSubmit(() => askConfirmData("save"))}
                    size="small"
                  >
                    Conferma
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default MedintAgendaPrestazioneNotesModal;
